import React, { useMemo, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
// import Packages from '../components/Home/Packages'; // Removed unused import
import UpcomingTrips from '../components/Home/UpcomingTrips';
import { fetchTripsByLocation } from '../functions/FetchTrips';
import '../styles/DestinationsPage.css';
import LogoSection from '../components/LogoSection';
import ContactCard from '../components/ContactCard';

const DestinationsPage = () => {
  const { destinationName } = useParams();
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Scroll to top when destination changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [destinationName]);

  // Fetch packages when destination changes
  useEffect(() => {
    const fetchPackages = async () => {
      if (!destinationName) return;
      
      setLoading(true);
      try {
        const locationPackages = await fetchTripsByLocation(destinationName);
        setPackages(locationPackages);
      } catch (error) {
        console.error('Error fetching packages:', error);
        setPackages([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPackages();
  }, [destinationName]);
  
  // Destination data mapping (memoized)
  const destinationsData = useMemo(() => ({
    kerala: {
      name: 'Kerala',
      tagline: 'God\'s Own Country',
      description: `Kerala, located on India's tropical Malabar Coast, is known for its palm-lined beaches, backwaters, and a network of canals. Inland are the Western Ghats, mountains with tea, coffee, and spice plantations as well as wildlife. Kerala offers a slower, more peaceful pace of life amidst stunning natural scenery.`,
      heroImage: 'https://images.unsplash.com/photo-1602216056096-3b40cc0c9944?auto=format&fit=crop&q=80&w=2000',
      attractions: [
        {
          name: 'Alleppey Backwaters',
          description: 'Experience the serene backwaters on a traditional houseboat cruise.',
          image: 'https://images.unsplash.com/photo-1602216056096-3b40cc0c9944?auto=format&fit=crop&q=80&w=1000'
        },
        {
          name: 'Munnar Tea Gardens',
          description: 'Explore lush green tea plantations set against picturesque hills.',
          image: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTLONVCcNamz-ZlKtVF6-AYu2pfb7nMtA5cPQ&s'
        },
        {
          name: 'Fort Kochi',
          description: 'Discover the colonial heritage and the famous Chinese fishing nets.',
          image: 'https://images.unsplash.com/photo-1590050752117-238cb0fb12b1?auto=format&fit=crop&q=80&w=1000'
        },
        {
          name: 'Wayanad Wildlife Sanctuary',
          description: 'Spot exotic wildlife in one of Kerala\'s most biodiverse regions.',
          image: 'https://images.unsplash.com/photo-1548013146-72479768bada?auto=format&fit=crop&q=80&w=1000'
        }
      ],
      bestTimeToVisit: 'October to March',
      languages: 'Malayalam, English',
      currency: 'Indian Rupee (INR)',
      mapEmbed: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d4016773.3361261715!2d74.43778234319685!3d10.534924747356094!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3b0812ffd49cf55b%3A0x64bd90fbed387c99!2sKerala!5e0!3m2!1sen!2sin!4v1651147690559!5m2!1sen!2sin'
    },
    rajasthan: {
      name: 'Rajasthan',
      tagline: 'Land of Kings',
      description: 'Rajasthan, known for its royal heritage and vibrant culture, is home to magnificent forts, opulent palaces, and colorful festivals. The state offers a unique blend of history, architecture, and desert landscapes that captivate visitors from around the world.',
      heroImage: 'https://images.unsplash.com/photo-1599661046289-e31897846e41?auto=format&fit=crop&q=80&w=2070',
      attractions: [
        {
          name: 'Jaipur Pink City',
          description: 'Explore the historic pink-hued old city with its grand palaces and bazaars.',
          image: 'https://images.unsplash.com/photo-1603262110263-fb0112e7cc33?auto=format&fit=crop&q=80&w=1000'
        },
        {
          name: 'Udaipur City Palace',
          description: 'Visit the majestic palace complex overlooking Lake Pichola.',
          image: 'https://s7ap1.scene7.com/is/image/incredibleindia/city-palace-udaipur-rajasthan-1-new-attr-hero?qlt=82&ts=1742171011440'
        },
        {
          name: 'Jaisalmer Fort',
          description: 'Experience the golden sandstone fortress rising from the desert landscape.',
          image: 'https://travelsetu.com/apps/uploads/new_destinations_photos/destination/2023/12/22/4230a8585027daa25a9f8ac504e7ad14_1000x1000.jpg'
        },
        {
          name: 'Thar Desert',
          description: 'Embark on a camel safari through the golden sand dunes.',
          image: 'https://static.toiimg.com/thumb/68427211/3.jpg?width=1200&height=900'
        }
      ],
      bestTimeToVisit: 'October to March',
      languages: 'Hindi, Rajasthani, English',
      currency: 'Indian Rupee (INR)',
      mapEmbed: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3650202.7534673587!2d71.40139059429882!3d26.62279920599757!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x396a3efaf7e30e37%3A0xb52b9b4506c088e5!2sRajasthan!5e0!3m2!1sen!2sin!4v1651147757523!5m2!1sen!2sin'
    },
    himachal: {
      name: 'Himachal',
      tagline: 'Land of Gods',
      description: 'Himachal Pradesh, nestled in the western Himalayas, is characterized by its stunning mountain landscapes, lush valleys, and pristine rivers. This northern Indian state offers a perfect blend of adventure activities, spiritual retreats, and colonial charm.',
      heroImage: 'https://images.unsplash.com/photo-1626621331169-5f34be280ed9?auto=format&fit=crop&q=80&w=2070',
      attractions: [
        {
          name: 'Shimla',
          description: 'Explore the charming colonial architecture of this former summer capital.',
          image: 'https://www.exoticmiles.com/wp-content/uploads/2020/05/Shimla-2.jpg'
        },
        {
          name: 'Manali',
          description: 'Adventure in this popular mountain resort surrounded by pine forests.',
          image: 'https://i.ytimg.com/vi/5VvjxmbWeKk/hq720.jpg?sqp=-oaymwEhCK4FEIIDSFryq4qpAxMIARUAAAAAGAElAADIQj0AgKJD&rs=AOn4CLBibMI18pG7EyKV0F_MbNk_0_T3cg'
        },
        {
          name: 'Dharamshala',
          description: 'Visit the home of the Dalai Lama and center of Tibetan culture.',
          image: 'https://images.unsplash.com/photo-1588083949404-c4f1ed1323b3?auto=format&fit=crop&q=80&w=1000'
        },
        {
          name: 'Spiti Valley',
          description: 'Discover the cold desert mountain valley with breathtaking landscapes.',
          image: 'https://static.toiimg.com/photo/49539118.cms'
        }
      ],
      bestTimeToVisit: 'March to June and September to November',
      languages: 'Hindi, Pahari, English',
      currency: 'Indian Rupee (INR)',
      mapEmbed: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1727215.0977025384!2d76.17283380375173!3d31.815603644025486!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390453c367f901f7%3A0x7cfe04c5564b7725!2sHimachal%20Pradesh!5e0!3m2!1sen!2sin!4v1651147836523!5m2!1sen!2sin'
    },
    kashmir: {
      name: 'Kashmir',
      tagline: 'Paradise on Earth',
      description: 'Kashmir is renowned for its stunning landscapes featuring snow-capped mountains, serene lakes, and lush valleys. The region offers a perfect blend of natural beauty and cultural richness, making it a dream destination for travelers seeking tranquility and adventure alike.',
      heroImage: 'https://hldak.mmtcdn.com/prod-s3-hld-hpcmsadmin/holidays/images/cities/3795/Gondola%204.jpeg',
      attractions: [
        {
          name: 'Dal Lake',
          description: 'Explore the iconic lake with its floating gardens and vibrant houseboats.',
          image: 'https://www.kashmironline.com/blog/wp-content/uploads/2022/05/dal.jpg'
        },
        {
          name: 'Gulmarg',
          description: 'Experience the beauty of this hill station known for skiing and the Gondola ride.',
          image: 'https://hldak.mmtcdn.com/prod-s3-hld-hpcmsadmin/holidays/images/cities/3795/Gondola%204.jpeg'
        },
        {
          name: 'Pahalgam',
          description: 'Discover the valley of shepherds with its picturesque landscapes and river.',
          image: 'https://images.unsplash.com/photo-1566837497312-7be7830ae9b1?auto=format&fit=crop&q=80&w=1000'
        },
        {
          name: 'Srinagar',
          description: 'Visit the summer capital with its gardens, lakes, and historic monuments.',
          image: 'https://upload.wikimedia.org/wikipedia/commons/thumb/a/a8/Houseboats%2C_Dal_Lake%2C_Kashmir.jpg/960px-Houseboats%2C_Dal_Lake%2C_Kashmir.jpg'
        }
      ],
      bestTimeToVisit: 'April to October',
      languages: 'Kashmiri, Urdu, Hindi, English',
      currency: 'Indian Rupee (INR)',
      mapEmbed: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1692927.725878836!2d74.32858556363037!3d33.93912282329395!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x38e1092499ffa89d%3A0x6567a6d4cbe9a695!2sJammu%20and%20Kashmir!5e0!3m2!1sen!2sin!4v1651147954453!5m2!1sen!2sin'
    },
    karnataka: {
      name: 'Karnataka',
      tagline: 'One State, Many Worlds',
      description: 'Karnataka offers a perfect blend of ancient heritage sites, modern cities, scenic hill stations, and pristine beaches. From the tech hub of Bangalore to the royal city of Mysore, from the ruins of Hampi to the beaches of Gokarna, Karnataka has something for every traveler.',
      heroImage: 'https://etimg.etb2bimg.com/photo/86750638.cms',
      attractions: [
        {
          name: 'Mysore Palace',
          description: 'Visit the magnificent royal residence known for its Indo-Saracenic architecture.',
          image: 'https://content.r9cdn.net/rimg/dimg/43/2c/fe92dfc3-lm-48454-167a97bd72f.jpg?width=1366&height=768&xhint=1488&yhint=1244&crop=true'
        },
        {
          name: 'Hampi',
          description: 'Explore the UNESCO World Heritage Site with its ancient ruins and temples.',
          image: 'https://hampitourism.co.in/images/v2/headers/hampi-tourism-hero.jpg'
        },
        {
          name: 'Coorg',
          description: 'Discover the hill station known for its coffee plantations and misty landscapes.',
          image: 'https://static.toiimg.com/thumb/104040262.cms?resizemode=75&width=1200&height=900'
        },
        {
          name: 'Jog Falls',
          description: 'Marvel at India\'s second-highest plunge waterfall, creating a spectacular vista.',
          image: 'https://blog.summitersadventures.com/uploads/images/2020/06/image_750x_5ee1a9b5877cd.jpg'
        }
      ],
      bestTimeToVisit: 'October to April',
      languages: 'Kannada, English, Hindi',
      currency: 'Indian Rupee (INR)',
      mapEmbed: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3887.3352326160365!2d77.59012661413822!3d13.007997390831518!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae1670c9b44e6d%3A0xf8dfc3e8517e4fe0!2sKarnataka!5e0!3m2!1sen!2sin!4v1651148022342!5m2!1sen!2sin'
    },
    uttarakhand: {
      name: 'Uttarakhand',
      tagline: 'Land of Gods',
      description: 'Uttarakhand, nestled in the lap of the Himalayas, is known for its spiritual significance, adventure opportunities, and breathtaking natural beauty. From the sacred rivers to the snow-capped peaks, from charming hill stations to wildlife sanctuaries, Uttarakhand offers a diverse range of experiences.',
      heroImage: 'https://uttarakhandtourism.gov.in/assets/media/GodGrace.jpg',
      attractions: [
        {
          name: 'Rishikesh',
          description: 'Experience the yoga capital of the world with its spiritual ambiance and adventure activities.',
          image: 'https://images.unsplash.com/photo-1593181629936-11c609b8db9b?auto=format&fit=crop&q=80&w=1000'
        },
        {
          name: 'Nainital',
          description: 'Explore the charming lake city surrounded by mountains and forests.',
          image: 'https://www.bontravelindia.com/wp-content/uploads/2022/08/Best-Places-to-Visit-in-Nainital-1000x565.jpg'
        },
        {
          name: 'Valley of Flowers',
          description: 'Discover the UNESCO World Heritage site known for its meadows of endemic alpine flowers.',
          image: 'https://upload.wikimedia.org/wikipedia/commons/2/21/%28A%29_Valley_of_flowers%2C_Garhwal_Uttarakhand_India.jpg'
        },
        {
          name: 'Badrinath',
          description: 'Visit one of the sacred Char Dham pilgrimage sites nestled in the Himalayan mountains.',
          image: 'https://www.chardham-pilgrimage-tour.com/assets/images/badrinath-banner3.webp'
        }
      ],
      bestTimeToVisit: 'March to June and September to November',
      languages: 'Hindi, Garhwali, Kumaoni, English',
      currency: 'Indian Rupee (INR)',
      mapEmbed: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3472.9718436114956!2d78.04240741515262!3d30.32459998177984!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390929c356c888af%3A0x4c3562c032518799!2sUttarakhand!5e0!3m2!1sen!2sin!4v1651148095823!5m2!1sen!2sin'
    },
    madhyapradesh: {
      name: 'Madhya Pradesh',
      tagline: 'The Heart of India',
      description: 'Madhya Pradesh, located in the heart of India, is a treasure trove of history, nature, and spirituality. From UNESCO World Heritage sites like Khajuraho and Sanchi to magnificent tiger reserves like Bandhavgarh and Kanha, the state offers a diverse range of experiences for all types of travelers.',
      heroImage: 'https://www.bandhavgarhnationalpark.in/uploads/best-places-madhya-pradesh.jpg',
      attractions: [
        {
          name: 'Khajuraho Temples',
          description: 'Explore the UNESCO World Heritage site known for its intricate carvings and Nagara-style architecture.',
          image: 'https://static.toiimg.com/photo/64665528.cms'
        },
        {
          name: 'Bandhavgarh National Park',
          description: 'Discover one of India\'s finest tiger reserves with its high density of Bengal tigers.',
          image: 'https://www.bandhavgarhnationalpark.in/uploads/jungle-safari-bandhavgarh.jpg'
        },
        {
          name: 'Sanchi Stupa',
          description: 'Visit the ancient Buddhist monument with its impressive carvings and historical significance.',
          image: 'https://images.travelandleisureasia.com/wp-content/uploads/sites/2/2021/02/22141026/5r4t-12.jpg'
        },
        {
          name: 'Orchha',
          description: 'Explore the medieval city with its grand palaces, temples, and cenotaphs along the Betwa River.',
          image: 'https://dynamic-media-cdn.tripadvisor.com/media/photo-o/16/1d/24/96/the-marvellous-orchha.jpg?w=900&h=500&s=1'
        }
      ],
      bestTimeToVisit: 'October to March',
      languages: 'Hindi, English',
      currency: 'Indian Rupee (INR)',
      mapEmbed: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3770142.1351171394!2d75.76840998776293!3d23.60135630721797!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39667381d35aea05%3A0x7f82fb5d0334f0c6!2sMadhya%20Pradesh!5e0!3m2!1sen!2sin!4v1651148165123!5m2!1sen!2sin'
    },
    tamilnadu: {
      name: 'Tamil Nadu',
      tagline: 'Enchanting Tamil Nadu',
      description: 'Tamil Nadu is a land of cultural and religious heritage, known for its Dravidian-style temples, classical dance forms, distinctive cuisine, and vibrant festivals. From the temple cities of Madurai and Thanjavur to the hill stations of Ooty and Kodaikanal, Tamil Nadu offers a rich tapestry of experiences.',
      heroImage: 'https://images.unsplash.com/photo-1582510003544-4d00b7f74220?auto=format&fit=crop&q=80&w=2070',
      attractions: [
        {
          name: 'Meenakshi Temple',
          description: 'Visit the iconic temple in Madurai known for its towering gopurams and intricate carvings.',
          image: 'https://chardhambooking.com/wp-content/uploads/2021/01/Meenakshi-Temple-ili-334-img-5.jpg'
        },
        {
          name: 'Marina Beach',
          description: 'Explore one of the longest urban beaches in the world located in Chennai.',
          image: 'https://s7ap1.scene7.com/is/image/incredibleindia/marina-beach-chennai-tamil-nadu-4-attr-hero?qlt=82&ts=1726655007714'
        },
        {
          name: 'Ooty',
          description: 'Discover the "Queen of Hill Stations" with its tea gardens, lakes, and pleasant climate.',
          image: 'https://www.trawell.in/blog/wp-content/uploads/2024/03/ooty-main-730x410.jpg'
        },
        {
          name: 'Thanjavur',
          description: 'Explore the cultural capital known for the Brihadeeswarar Temple and Saraswati Mahal Library.',
          image: 'https://mediaim.expedia.com/destination/1/c356c905de5af9245f36a4374bd5cbea.jpg'
        }
      ],
      bestTimeToVisit: 'October to March',
      languages: 'Tamil, English',
      currency: 'Indian Rupee (INR)',
      mapEmbed: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d4006398.694705211!2d76.2049578019277!3d10.824097545398702!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3b00c582b1189633%3A0x559475cc463361f0!2sTamil%20Nadu!5e0!3m2!1sen!2sin!4v1651148165843!5m2!1sen!2sin'
    },
    // International Destinations 
    bali: {
      name: 'Bali',
      tagline: 'Island of the Gods',
      description: 'Bali, Indonesia’s most famous island, is renowned for its forested volcanic mountains, iconic rice paddies, beaches, and coral reefs. The island is also known for its yoga and meditation retreats, vibrant nightlife, and rich culture.',
      heroImage: 'https://images.unsplash.com/photo-1573790387438-4da905039392?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      attractions: [
        {
          name: 'Ubud Monkey Forest',
          description: 'A lush forest sanctuary home to hundreds of monkeys and ancient temples.',
          image: 'https://sp.yimg.com/ib/th?id=OADD2.8452573628468_14ZJOVU4FOLZAL1QLG&pid=21.2&c=16&roil=0&roit=0&roir=1&roib=1&w=442&h=231'
        },
        {
          name: 'Tanah Lot Temple',
          description: 'A picturesque sea temple perched on a rock formation.',
          image: 'https://up.yimg.com/ib/th?asid=432345564363836766&id=OAUMA.D3DD235F0C18C11C412D4E4FB569308E_A85413A521D9771C&pid=21.1&o=5&w=442&h=231&c=1&rs=1&qlt=95'
        },
        {
          name: 'Tegallalang Rice Terraces',
          description: 'Famous for its beautiful scenes of rice paddies involving the subak (traditional Balinese cooperative irrigation system).',
          image: 'https://sp.yimg.com/ib/th?id=OADD2.8452611294702_1753Z7Z8FM4S61CZ0Y&pid=21.2&c=16&roil=0&roit=0&roir=1&roib=1&w=442&h=231'
        },
        {
          name: 'Uluwatu Temple',
          description: 'A sea temple set on a cliff, famous for its sunset views and Kecak dance performances.',
          image: 'https://up.yimg.com/ib/th?asid=432345565572753499&id=OAUMA.4FE46C9A80E0430EBB1E2BFC65034BAC_7C3D0B5BD201E461&pid=21.1&o=5&w=442&h=231&c=1&rs=1&qlt=95'
        }
      ],
      bestTimeToVisit: 'April to October',
      languages: 'Balinese, Indonesian, English',
      currency: 'Indonesian Rupiah (IDR)',
      mapEmbed: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d126762.7263714876!2d115.0919506!3d-8.3405389!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x2dd219c2b2b2b2b3%3A0x3030bfbcaf770b0!2sBali%2C%20Indonesia!5e0!3m2!1sen!2sin!4v1651149000000!5m2!1sen!2sin'
    },
    dubai: {
      name: 'Dubai',
      tagline: 'City of Gold',
      description: 'Dubai is a city and emirate in the United Arab Emirates known for luxury shopping, ultramodern architecture, and a lively nightlife scene. Burj Khalifa, an 830m tall tower, dominates the skyscraper-filled skyline.',
      heroImage: 'https://images.unsplash.com/photo-1518684079-3c830dcef090?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      attractions: [
        {
          name: 'Burj Khalifa',
          description: 'The world’s tallest building with an observation deck offering panoramic city views.',
          image: 'https://arabiahorizons.com/blog/wp-content/uploads/2020/06/Burj-Khalifa-Floors.jpg'
        },
        {
          name: 'Dubai Mall',
          description: 'One of the world’s largest shopping malls with an aquarium and ice rink.',
          image: 'https://s.yimg.com/zb/imgv1/f497b2ed-dada-377d-8440-080527ebca80/t_500x300'
        },
        {
          name: 'Desert Safari',
          description: 'Experience dune bashing, camel rides, and traditional entertainment in the desert.',
          image: 'https://bearinforest.com/wp-content/uploads/2020/01/safari-022-scaled.jpg'
        },
        {
          name: 'Palm Jumeirah',
          description: 'A man-made island known for luxury hotels, beaches, and the Atlantis resort.',
          image: 'https://bonjourdubai.fr/wp-content/uploads/2022/02/que-voir-a-palm-jumeirah.jpg'
        }
      ],
      bestTimeToVisit: 'November to March',
      languages: 'Arabic, English',
      currency: 'UAE Dirham (AED)',
      mapEmbed: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d115625.60804970547!2d55.0272906!3d25.2048493!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f434e2f6e6e6f%3A0x8e8e8e8e8e8e8e8e!2sDubai%2C%20UAE!5e0!3m2!1sen!2sin!4v1651149000001!5m2!1sen!2sin'
    },
    vietnam: {
      name: 'Vietnam',
      tagline: 'Timeless Charm',
      description: 'Vietnam is known for its beaches, rivers, Buddhist pagodas, and bustling cities. From the limestone islands of Ha Long Bay to the vibrant streets of Ho Chi Minh City, Vietnam offers a unique blend of natural beauty and cultural heritage.',
      heroImage: 'https://images.unsplash.com/photo-1528127269322-539801943592?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      attractions: [
        {
          name: 'Ha Long Bay',
          description: 'Famous for emerald waters and thousands of towering limestone islands topped with rainforests.',
          image: 'https://images5.alphacoders.com/390/thumb-1920-390620.jpg'
        },
        {
          name: 'Hoi An Ancient Town',
          description: 'A well-preserved Southeast Asian trading port dating from the 15th to the 19th century.',
          image: 'https://alsaharhoian.com/wp-content/uploads/2022/11/4255_11d-130_Copy.jpg'
        },
        {
          name: 'Ho Chi Minh City',
          description: 'Vietnam’s bustling southern city known for its French colonial landmarks.',
          image: 'https://www.tripsavvy.com/thmb/I-ZzBbYAsPCpkbh6Qcpch81R9d4=/1998x1500/filters:fill(auto,1)/ho-chi-minh-city-in-vietnam-at-night-524711494-ac666e60b3af49148e5b5936ea3ec7cc.jpg'
        },
        {
          name: 'Sapa Terraces',
          description: 'Stunning rice terraces and ethnic minority villages in northern Vietnam.',
          image: 'https://daytoursapa.com/wp-content/uploads/rice-terrace-sapa-2.jpg'
        }
      ],
      bestTimeToVisit: 'February to April and August to October',
      languages: 'Vietnamese, English',
      currency: 'Vietnamese Dong (VND)',
      mapEmbed: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3918967.019395029!2d105.318756!3d14.058324!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3135ab8c0b0b0b0b%3A0x3030bfbcaf770b0!2sVietnam!5e0!3m2!1sen!2sin!4v1651149000002!5m2!1sen!2sin'
    },
    thailand: {
      name: 'Thailand',
      tagline: 'Land of Smiles',
      description: 'Thailand is known for tropical beaches, opulent royal palaces, ancient ruins, and ornate temples displaying figures of Buddha. Bangkok, the capital, is an ultramodern cityscape beside quiet canalside communities.',
      heroImage: 'https://images.unsplash.com/photo-1528181304800-259b08848526?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      attractions: [
        {
          name: 'Phuket Beaches',
          description: 'Famous for white sand beaches and vibrant nightlife.',
          image: 'https://a.cdn-hotels.com/gdcs/production113/d940/a239798a-3c36-4ba7-8293-1b342ed5b8e8.jpg'
        },
        {
          name: 'Bangkok Grand Palace',
          description: 'A complex of buildings at the heart of Bangkok, the official residence of the Kings of Siam.',
          image: 'https://thecreativeadventurer.com/wp-content/uploads/2020/04/Bangkok-Grand-Palace22-1.jpg'
        },
        {
          name: 'Chiang Mai Temples',
          description: 'Explore ancient temples and the old city walls.',
          image: 'https://images.unsplash.com/photo-1528181304800-259b08848526?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
        },
        {
          name: 'Ayutthaya Historical Park',
          description: 'Ruins of the old capital with impressive temples and statues.',
          image: 'https://assets.traveltriangle.com/blog/wp-content/uploads/2018/11/About-Ayutthaya-Historical-Park.jpg'
        }
      ],
      bestTimeToVisit: 'November to early April',
      languages: 'Thai, English',
      currency: 'Thai Baht (THB)',
      mapEmbed: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3875837.019395029!2d100.992541!3d15.870032!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x30e29edb0b0b0b0b%3A0x3030bfbcaf770b0!2sThailand!5e0!3m2!1sen!2sin!4v1651149000003!5m2!1sen!2sin'
    },
    singapore: {
      name: 'Singapore',
      tagline: 'The Lion City',
      description: 'Singapore is a global financial center with a multicultural population. It is famous for its clean streets, modern skyline, lush gardens, and vibrant food scene.',
      heroImage: 'https://images.unsplash.com/photo-1525625293386-3f8f99389edd?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      attractions: [
        {
          name: 'Marina Bay Sands',
          description: 'Iconic hotel with a rooftop infinity pool and observation deck.',
          image: 'http://id.marinabaysands.com/content/dam/marinabaysands/guides/exceptional-experiences/architecture-of-mbs/masthead.jpg'
        },
        {
          name: 'Gardens by the Bay',
          description: 'Futuristic park with Supertree structures and biodomes.',
          image: 'https://www.shoreexcursionsgroup.com/img/tour/ASSPGARDENS-2.jpg'
        },
        {
          name: 'Sentosa Island',
          description: 'Resort island with beaches, theme parks, and attractions.',
          image: 'https://www.eligasht.co.uk/Blog/wp-content/uploads/2019/04/Sentosa-island.jpg'
        },
        {
          name: 'Singapore Zoo',
          description: 'World-renowned zoo with open enclosures and diverse wildlife.',
          image: 'https://mustsharenews.com/wp-content/uploads/2021/10/mandai-wildlife-reserve-1.jpg'
        }
      ],
      bestTimeToVisit: 'February to April',
      languages: 'English, Malay, Mandarin, Tamil',
      currency: 'Singapore Dollar (SGD)',
      mapEmbed: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d159656.1600240527!2d103.704165!3d1.352083!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x31da19b0b0b0b0b0%3A0x3030bfbcaf770b0!2sSingapore!5e0!3m2!1sen!2sin!4v1651149000004!5m2!1sen!2sin'
    },
    malaysia: {
      name: 'Malaysia',
      tagline: 'Truly Asia',
      description: 'Malaysia is known for its beaches, rainforests, and mix of Malay, Chinese, Indian and European cultural influences. Kuala Lumpur, the capital, is home to colonial buildings, busy shopping districts, and skyscrapers such as the iconic Petronas Twin Towers.',
      heroImage: 'https://console.kr-asia.com/wp-content/uploads/2022/05/esmonde-yong-9B08uduMyY-unsplash-min-scaled.jpg',
      attractions: [
        {
          name: 'Petronas Twin Towers',
          description: 'The world’s tallest twin towers and a symbol of modern Malaysia.',
          image: 'https://cdn.britannica.com/69/84969-050-D0E5FB34/buildings-Petronas-Twin-Towers-world-Kuala-Lumpur.jpg'
        },
        {
          name: 'Langkawi',
          description: 'An archipelago known for its beaches, clear waters, and duty-free shopping.',
          image: 'https://www.mustseespots.com/malaysia/img/langkawi-sky-bridge.jpg'
        },
        {
          name: 'Batu Caves',
          description: 'A limestone hill with a series of caves and cave temples.',
          image: 'https://www.nothingfamiliar.com/wp-content/uploads/2019/01/Batu-Caves-Dress-Code-1440x960.jpg'
        },
        {
          name: 'Penang Street Art',
          description: 'Famous murals and street food in the heritage city of George Town.',
          image: 'https://www.expatolife.com/wp-content/uploads/2019/03/Penang-street-art-Selling.jpg'
        }
      ],
      bestTimeToVisit: 'March to October',
      languages: 'Malay, English',
      currency: 'Malaysian Ringgit (MYR)',
      mapEmbed: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d159656.1600240527!2d101.975766!3d4.210484!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x31da19b0b0b0b0b0%3A0x3030bfbcaf770b0!2sMalaysia!5e0!3m2!1sen!2sin!4v1651149000005!5m2!1sen!2sin'
    },
    bhutan: {
      name: 'Bhutan',
      tagline: 'Land of the Thunder Dragon',
      description: 'Bhutan is a Buddhist kingdom on the Himalayas’ eastern edge, known for its monasteries, fortresses (dzongs), and dramatic landscapes that range from subtropical plains to steep mountains and valleys.',
      heroImage: 'https://www.tourmyindia.com/blog//wp-content/uploads/2022/10/Best-Places-to-Visit-in-Bhutan-Tourism.jpg',
      attractions: [
        {
          name: 'Paro Taktsang (Tiger’s Nest Monastery)',
          description: 'A sacred Buddhist site perched on a cliffside.',
          image: 'https://www.tourmyindia.com/blog//wp-content/uploads/2022/10/Best-Places-to-Visit-in-Bhutan-Tourism.jpg'
        },
        {
          name: 'Punakha Dzong',
          description: 'A majestic fortress at the confluence of two rivers.',
          image: 'https://i.pinimg.com/originals/77/6a/a7/776aa7b2f3cdfb019b29ee2548d60e3d.jpg'
        },
        {
          name: 'Thimphu',
          description: 'The capital city, blending modernity with tradition.',
          image: 'https://rtc.bt/images/Thimphu-City-Image-Main.jpg'
        },
        {
          name: 'Dochula Pass',
          description: 'A mountain pass with panoramic views and 108 memorial chortens.',
          image: 'https://northbengaltourism.com/images/bhutan/dochula_pass_1024.jpg'
        }
      ],
      bestTimeToVisit: 'March to May and September to November',
      languages: 'Dzongkha, English',
      currency: 'Bhutanese Ngultrum (BTN), Indian Rupee (INR)',
      mapEmbed: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3536517.019395029!2d90.433601!3d27.514162!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39e1940b0b0b0b0b%3A0x3030bfbcaf770b0!2sBhutan!5e0!3m2!1sen!2sin!4v1651149000006!5m2!1sen!2sin'
    }
  }), []);

  // Get destination data from URL parameter
  const destination = useMemo(() => {
    if (!destinationName) return destinationsData.kerala; // Default to Kerala if no param
    
    const key = destinationName.toLowerCase();
    if (destinationsData[key]) {
      return destinationsData[key];
    } else {
      // Redirect to home if destination not found
      setTimeout(() => navigate('/'), 0);
      return destinationsData.kerala; // Fallback while redirecting
    }
  }, [destinationName, navigate, destinationsData]); // Added destinationsData to dependencies

  if (!destination) return null;

  return (
    <>
      <Header />
      
      <div className="destination-page">
        {/* Hero Section */}
        <div className="destination-hero" style={{ backgroundImage: `url(${destination.heroImage})` }}>
          <div className="destination-hero-overlay">
            <div className="container">
              <h1 className="destination-title">{destination.name}</h1>
              <p className="destination-tagline">{destination.tagline}</p>
            </div>
          </div>
        </div>

        {/* Overview Section */}
        <section className="destination-overview py-5">
          <div className="container">
            <div className="row">
              <div className="col-lg-8">
                <h2 className="section-title">Discover {destination.name}</h2>
                <p className="destination-description">{destination.description}</p>
                
                <div className="destination-info mt-4">
                  <div className="info-item">
                    <i className="fas fa-calendar-alt"></i>
                    <div>
                      <h5>Best Time to Visit</h5>
                      <p>{destination.bestTimeToVisit}</p>
                    </div>
                  </div>
                  <div className="info-item">
                    <i className="fas fa-language"></i>
                    <div>
                      <h5>Languages</h5>
                      <p>{destination.languages}</p>
                    </div>
                  </div>
                  <div className="info-item">
                    <i className="fas fa-rupee-sign"></i>
                    <div>
                      <h5>Currency</h5>
                      <p>{destination.currency}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="col-lg-4">
                <ContactCard hideLabels={true} />
              </div>
            </div>
          </div>
        </section>

        {/* Packages Section - Show only trip cards using UpcomingTrips */}
        <section className="destination-packages py-5">
          <div className="container">
            <UpcomingTrips
              hideControls={true}
              trips={packages}
              loading={loading}
            />
          </div>
        </section>

        {/* Attractions Section */}
        <section className="destination-attractions py-5">
          <div className="container">
            <h2 className="section-title text-center mb-5">Top Attractions in {destination.name}</h2>
            
            <div className="row">
              {destination.attractions.map((attraction, index) => (
                <div className="col-md-6 col-lg-3 mb-4" key={index}>
                  <div className="attraction-card">
                    <div className="attraction-image">
                      <img src={attraction.image} alt={attraction.name} />
                    </div>
                    <div className="attraction-content">
                      <h4>{attraction.name}</h4>
                      <p>{attraction.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <section className="destination-cta py-5">
          <div className="container text-center">
            <h2>Ready to explore the beauty of {destination.name}?</h2>
            <p>Book your dream vacation today and create memories that last a lifetime.</p>
            <button
              className="btn btn-primary btn-lg mt-3"
              onClick={() => {
                if (window.showContactCardOverlay) window.showContactCardOverlay();
              }}
            >
              Plan Your Trip Now
            </button>
          </div>
        </section>
      </div>

      <LogoSection />
      <Footer />
    </>
  );
};

export default DestinationsPage;